import * as React from 'react';
import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0,
  withAuthenticationRequired,
  WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import { ROUTE_PATH, setAuthTokenFromCookie } from '../constants';
import { useDispatch, useSelector } from 'react-redux';
import { SESSION_ACTIONS } from '../actions/session';
import { sessionSelector } from '../reducers/session';
import { Route, Switch, useHistory } from 'react-router-dom';
import { SessionStorageKey } from '../types';

export const ContentRoot: React.FC = ({ children }) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useDispatch();
  const isLegacyLoginPage = useSelector(sessionSelector.isLegacyLoginPage);
  const logPath = isLegacyLoginPage ? '' : ROUTE_PATH.LOGIN;

  React.useEffect(() => {
    const secondaryToken = sessionStorage.getItem(
      SessionStorageKey.SECONDARY_TOKEN,
    );
    const fetchToken = async () => {
      const accessToken = await getAccessTokenSilently();
      const headers = new Headers();
      headers.set('Authorization', `Bearer ${accessToken}`);

      const res = await fetch('/api/v2/userinfo', {
        headers: headers,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.detail);
      } else {
        return await res.json();
      }
    };
    // We dont want to call /v2/userinfo if the secondary token is set
    // As of now, logging in as AW employee who is not part of any organization
    // after the customer switch, upon refresh will sent back to customer switch
    // which is not ideal.
    if (!secondaryToken) {
      fetchToken()
        .then((res) => {
          setAuthTokenFromCookie();
          dispatch(SESSION_ACTIONS.LOGIN(res));
        })
        .catch((err) => {
          dispatch(SESSION_ACTIONS.SET_LOGIN_ERROR(err.message));
          logout({
            returnTo: `${window.location.origin}${logPath}`,
            client_id: process.env.REACT_APP_AUTH0_CLIENT_ID,
          });
        });
    }
  }, [dispatch, getAccessTokenSilently, logout, logPath]);

  return <>{children}</>;
};

export type AwthProviderProps = {
  auth0Config: Auth0ProviderOptions;
  loginConfig: WithAuthenticationRequiredOptions;
  unAuthenticatedRoute?: React.ReactNode;
  unAuthenticatedNode?: React.ReactNode;
};

export const AwthProvider: React.FC<AwthProviderProps> = ({
  children,
  auth0Config,
  loginConfig,
  unAuthenticatedRoute,
  unAuthenticatedNode,
}) => {
  const AuthenticatedContentRoot = React.useMemo(
    () => withAuthenticationRequired(ContentRoot, loginConfig),
    [loginConfig],
  );
  const loginEmail = useSelector(sessionSelector.loginEmail);

  const history = useHistory();

  const onRedirectCallback = React.useCallback(
    (appState?: AppState) => {
      if (appState?.returnTo) {
        history.push(appState.returnTo);
      }
    },
    [history],
  );

  return (
    <Auth0Provider
      {...auth0Config}
      login_hint={loginEmail == null ? '' : loginEmail}
      onRedirectCallback={onRedirectCallback}
    >
      {unAuthenticatedNode}
      <Switch>
        {unAuthenticatedRoute}
        <Route>
          <AuthenticatedContentRoot>{children}</AuthenticatedContentRoot>
        </Route>
      </Switch>
    </Auth0Provider>
  );
};
